import { pack } from '@attain-apps/data-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import type { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CommonArgs, standardNodeCreator } from './utils'
/**
 * Returns first non-null value in the given list of expressions
 *
 * If a single expression is given instead of a list, a default value is
 * inferred based on the type of that expression. __NOTE:__ this does not apply
 * if given a list of expressions with only one item.
 *
 * @param expressions Expressions are evaluated in order, and the first non-null
 * value is returned
 * @example
 * { '??': [ { '@.': '!descr' }, 'No description provided' ] }
 * // ->
 * 'This unit has a description',
 * 'No description provided',
 * 'No description provided',
 * 'This unit also has a description',
 * 'No description provided',
 * ...
 *
 * // Using defaults
 * { '??': { '@.': '!amount' } }
 * // ->
 * 0
 *
 * { '??': { '@.': '!descr' } }
 * // ->
 * ''
 *
 * { '??': { '@.': '!is_foo' } }
 * // ->
 * false
 *
 * { '??': { '@.': '!items' } }
 * // ->
 * []
 *
 * { '??': { '@.': '!obj' } }
 * // ->
 * {}
 */
export interface Coalesce<out T = any> {
  // TODO: the below type should be `Maybe.ReadonlyArray<Expression<T>>`, but
  // that introduces downstream issues as of 9/8/2025
  /** {@link Coalesce} */
  readonly [Key.Coalesce]: readonly Expression<T>[] | Expression<any>
}
export namespace Coalesce {
  export const isCoalesce: TypeChecker<Coalesce> = TypeChecker.Factory.lazy(
    () =>
      TypeChecker.Factory.object<Coalesce>({
        [Key.Coalesce]: CommonArgs.isListOfExpressions,
      }),
  ).usingAlias('Fn.Coalesce')
  export const assertIsCoalesce: TypeAsserter<Coalesce> =
    TypeAsserter.Factory.from(isCoalesce)

  /**
   * @see {@link Node}
   */
  export class Node<T = unknown> extends fnNode.Node<
    T,
    Key.Coalesce,
    readonly expressionNode.Node[]
  > {
    // NOTE: done as getter in case underlying nodes have getters
    public get type(): common.value.types.Type | undefined {
      return common.value.types.Intrinsic.Generalized.from(
        ...this.arg.map(node => node.type).filter(TypeChecker.isNotNullish.raw),
      )
    }

    constructor(arg: Node<T>['arg']) {
      super(Key.Coalesce, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  const expressionsInArgs: FnHelper<Coalesce>['expressionsInArgs'] = fn =>
    pack(fn[Key.Coalesce])
  export const HELPER: FnHelper<Coalesce> = {
    createNode: standardNodeCreator.usingExpressionList<
      Coalesce,
      Node['arg'],
      {
        readonly shouldAddDefaultValue: boolean
        nonNullValueNode?: expressionNode.Node<unknown>
      }
    >({
      // If there is nothing to coalesce, the result is null
      emptyValue: null,
      finalizeArgs: (args, stash, context) => [
        ...args,
        // If we have a non-nullish value to terminate in, then we terminate
        // with that
        ...(stash.nonNullValueNode
          ? [stash.nonNullValueNode]
          : stash.shouldAddDefaultValue
            ? _determineDefaultValue(args[0])
            : []),
      ],
      getArgExpressions: expressionsInArgs,
      handleValue: (value, stash) => {
        // Once we encounter a non-nullish value, we can make that the last
        // value in our coalesce expression and ignore everything else. E.g.,
        // COALESCE(A, B, 100, C, D) is the same as COALESCE(A, B, 100)
        if (TypeChecker.isNotNullish(value.value)) {
          stash.nonNullValueNode = value
          return { break: true } as const
        }
        return undefined
      },
      nodeClass: Node,
      setupStash: fn => ({
        shouldAddDefaultValue: !TypeChecker.isReadonlyArray(fn[Key.Coalesce]),
      }),
    }),
    expressionsInArgs,
    typeChecker: isCoalesce,
  }
}

// NOTE: returns as list for convenience above
function _determineDefaultValue(
  node: expressionNode.Node,
): readonly expressionNode.ForValue<unknown>[] {
  const defaultValue = common.value.Raw.defaultFor(node.type)
  return defaultValue === undefined
    ? []
    : [new expressionNode.ForValue(defaultValue)]
}
